import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import IndividualLimitGuidance from './IndividualLimitGuidance';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';

const emptyUser = () => ({
    name: '',
    email: '',
    role: '',
    maxAuthValue: 0,
});

export default function CreateUserDialog({ children, onSave, isCustom = false }) {

    const [open, setOpen] = useState(false);
    const [user, setUser] = useState(emptyUser());
    const [isChecked, setIsChecked] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setUser(emptyUser());
            setIsChecked(false);
            setError('');
        }
    }, [open]);

    const isValid = isChecked
        && user.name.trim()
        && user.email.trim()
        && user.role
        && user.maxAuthValue > 0;

    const handleSubmit = async () => {
        if (!isValid) {
            setError('Please complete all fields and accept the permission statement.');
            return;
        }

        const ok = await onSave({
            name: user.name.trim(),
            email: user.email.trim(),
            role: user.role,
            maxAuthValue: user.maxAuthValue,
        });

        if (ok) {
            setOpen(false);
        } else {
            setError('Something went wrong creating the user.');
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Create User</DialogTitle>
                    <DialogDescription>
                        Add a new user who can approve payments on behalf of your organisation.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-4 py-2">
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="user-name">Name</Label>
                        <Input
                            id="user-name"
                            placeholder="Full name"
                            value={user.name}
                            onChange={(e) => setUser({ ...user, name: e.target.value })}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label htmlFor="user-email">Email</Label>
                        <Input
                            id="user-email"
                            type="email"
                            placeholder="Email address"
                            value={user.email}
                            onChange={(e) => setUser({ ...user, email: e.target.value })}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label>Role</Label>
                        <Select value={user.role} onValueChange={(value) => setUser({ ...user, role: value })}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    <SelectItem value="User">User</SelectItem>
                                    <SelectItem value="Admin">Admin</SelectItem>
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label htmlFor="user-max-auth">
                            {isCustom === null ? 'Individual limit' : isCustom ? 'Individual limit (standard approvals)' : 'Max Auth Value'}
                        </Label>
                        <Input
                            id="user-max-auth"
                            aria-describedby={isCustom === null ? undefined : 'create-user-limit-guidance'}
                            type="number"
                            placeholder={isCustom === null ? 'Individual limit' : 'Max Auth Value'}
                            value={user.maxAuthValue || ''}
                            onChange={(e) => setUser({
                                ...user,
                                maxAuthValue: e.target.value === '' ? 0 : parseInt(e.target.value, 10),
                            })}
                        />
                        {isCustom !== null && <IndividualLimitGuidance isCustom={isCustom} id="create-user-limit-guidance" />}
                    </div>

                    <div className="flex items-start gap-2">
                        <Checkbox
                            id="user-terms"
                            checked={isChecked}
                            onCheckedChange={() => setIsChecked(!isChecked)}
                        />
                        <label htmlFor="user-terms" className="text-sm leading-snug text-gray-600">
                            By submitting this information, you grant permission for this individual to authorise
                            payments on behalf of your organisation and confirm you are authorised to do so.
                        </label>
                    </div>

                    {error && <p className="text-sm text-red-600 mb-0">{error}</p>}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button className="bg-yavrio-green" disabled={!isValid} onClick={handleSubmit}>
                        Create User
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
